import {
  combineSorFilters,
  type SorQueryPayload,
  type SorRecord,
} from '../hooks/useSorApi';
import type {
  WorkQueueCreateOption,
  WorkQueueFilterOption,
  WorkQueueItem,
  WorkQueueItemStatus,
} from './myWorkQueueTypes';

/** SOR definition holding work queue items (`data` = QueueItemRecordData) */
export const QUEUE_ITEM_DEFINITION = 'core.workqueue.queue_item';

/** Reference fields resolved into each queue item's `expanded` */
export const QUEUE_ITEM_EXPAND = [
  'sourceDefinition',
  'queue',
  'lastAction',
  'claimant',
] as const;

export type QueueItemExpandKey = (typeof QUEUE_ITEM_EXPAND)[number];

/** Lifecycle states of a queue item record, for the state filter */
export const QUEUE_ITEM_STATES = [
  'READY',
  'ASSIGNED',
  'IN_PROGRESS',
  'WAITING',
  'COMPLETED',
  'CANCELLED',
] as const;

/** Dropdown options for QUEUE_ITEM_STATES; key is the value sent as the query `state` */
export const QUEUE_ITEM_STATE_OPTIONS: WorkQueueFilterOption[] =
  QUEUE_ITEM_STATES.map((state) => ({
    key: state,
    label: state
      .toLowerCase()
      .split('_')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' '),
  }));

/** What the user has narrowed the list to; empty values add no condition */
export interface QueueItemQueryCriteria {
  /** Matched against `data.title` (contains), e.g. "CE-PILOT" */
  search?: string;
  /** Limit to one queue (`data.queueId`) */
  queueId?: string;
  /** Lifecycle state, e.g. "READY", sent as a `state eq` filter condition; omit for every state */
  state?: string | null;
  /**
   * Leave CANCELLED items out on the server (record `state` ne CANCELLED). A queue item is re-created
   * each cycle and earlier cycles are cancelled, so without this most of the payload is hidden anyway.
   */
  excludeCancelled?: boolean;
}

/** Builds the POST /api/v1/records/query payload (without `page`) from the current criteria */
export function buildQueueItemsQuery({
  search,
  queueId,
  state,
  excludeCancelled = false,
}: QueueItemQueryCriteria = {}): Omit<SorQueryPayload, 'page'> {
  const searchText = search?.trim();
  return {
    definition: QUEUE_ITEM_DEFINITION,
    filter: combineSorFilters([
      !!searchText && {
        field: 'data.title',
        op: 'contains',
        value: searchText,
      },
      !!queueId && { field: 'data.queueId', op: 'eq', value: queueId },
      state
        ? { field: 'state', op: 'eq', value: state }
        : excludeCancelled && { field: 'state', op: 'ne', value: 'CANCELLED' },
    ]),
    search: null,
    // The state is filtered through `filter` above, not the top-level `state` param
    state: null,
    include_deleted: false,
    expand: [...QUEUE_ITEM_EXPAND],
  };
}

/** `data` payload of a `core.workqueue.queue_item` SOR record */
export interface QueueItemRecordData {
  title: string;
  purpose: string;
  queueId: string;
  queueVisitId: string;
  correlationId: string;
  sourceRecordId: string;
  sourceDefinitionId: string;
  cycle?: number;
  priority?: number;
  enqueuedAt?: string;
  queueEnteredAt?: string;
  dueAt?: string | null;
  followUpAt?: string | null;
  assignedAt?: string | null;
  claimedAt?: string | null;
  startedAt?: string | null;
  completedAt?: string | null;
  cancelledAt?: string | null;
  claimedByUserId?: string | null;
  assigneeMemberId?: string | null;
  outcome?: string | null;
  waitingReason?: string | null;
  cancellationReason?: string | null;
  lastActionId?: string | null;
  appliedPolicyKey?: string | null;
  appliedQueueRevision?: number | null;
}

/** A resolved reference in `expanded`, e.g. { display: "Inspection", key: "slm.insp.inspection" } */
export interface SorExpandedReference {
  id: string;
  definition: string;
  display: string | null;
  state: string | null;
  /** Set on definition references such as `sourceDefinition` */
  key?: string;
}

/** A queue item's resolved references, keyed by QUEUE_ITEM_EXPAND; null when the reference is unset */
export type QueueItemExpanded = Partial<
  Record<QueueItemExpandKey, SorExpandedReference | null>
>;

export type QueueItemRecord = SorRecord<QueueItemRecordData> & {
  expanded?: QueueItemExpanded;
};

/** SOR definition listing the entity types routed to work queues (`data` = QueueEntityRecordData) */
export const QUEUE_ENTITY_DEFINITION = 'core.workqueue.queue_entities';

/** `data` payload of a `core.workqueue.queue_entities` record */
export interface QueueEntityRecordData {
  /** Off stops routing for the entity; missing counts as on */
  enabled?: boolean;
  description?: string;
  definitionId: string;
}

export type QueueEntityRecord = SorRecord<QueueEntityRecordData> & {
  expanded?: { definition?: SorExpandedReference | null };
};

/** Builds the query (without `page`) for every queue entity, with its definition resolved for the name */
export function buildQueueEntitiesQuery(): Omit<SorQueryPayload, 'page'> {
  return {
    definition: QUEUE_ENTITY_DEFINITION,
    filter: null,
    search: null,
    state: null,
    include_deleted: false,
    expand: ['definition'],
  };
}

/** Enabled queue entities -> one create option each, sorted by label */
export function mapQueueEntityCreateOptions(
  records: QueueEntityRecord[]
): WorkQueueCreateOption[] {
  return records
    .filter((record) => record.data?.enabled !== false)
    .map((record) => {
      const definition = record.expanded?.definition;
      const definitionId = record.data?.definitionId ?? definition?.id ?? '';
      const key = definition?.key ?? '';
      return {
        definitionId,
        key,
        label:
          definition?.display || record.data?.description || key || definitionId,
      };
    })
    .filter((option) => option.definitionId)
    .sort((a, b) => a.label.localeCompare(b.label));
}

// READY (and anything unknown) is not started
const COMPLETED_STATES = new Set(['COMPLETED']);
const CANCELLED_STATES = new Set(['CANCELLED']);
const IN_PROGRESS_STATES = new Set(['ASSIGNED', 'IN_PROGRESS', 'WAITING']);

/** Cancelled items have no status in My Work Queue, so they're left out of the list */
export function isQueueItemCancelled(
  record: Pick<QueueItemRecord, 'state' | 'data'>
): boolean {
  return (
    CANCELLED_STATES.has((record.state ?? '').toUpperCase()) ||
    !!record.data?.cancelledAt
  );
}

/** Lifecycle state first, then the item's timestamps */
export function getQueueItemStatus(
  record: Pick<QueueItemRecord, 'state' | 'data'>
): WorkQueueItemStatus {
  const state = (record.state ?? '').toUpperCase();
  const data = record.data ?? ({} as Partial<QueueItemRecordData>);
  if (isQueueItemCancelled(record)) {
    return 'cancelled';
  }
  if (COMPLETED_STATES.has(state) || data.completedAt) {
    return 'completed';
  }
  if (
    IN_PROGRESS_STATES.has(state) ||
    data.startedAt ||
    data.claimedAt ||
    data.assignedAt
  ) {
    return 'in_progress';
  }
  return 'not_started';
}

/**
 * "Inspection CE-PILOT-001" with purpose "inspection_review" -> "CE-PILOT-001".
 * Strips the leading entity word the title repeats from the purpose; otherwise keeps the full title.
 */
export function getQueueItemRequestNumber(
  title: string,
  purpose?: string
): string {
  const entityWord = purpose?.split('_')[0];
  if (entityWord) {
    const stripped = title
      .replace(new RegExp(`^${entityWord}s?\\s+`, 'i'), '')
      .trim();
    if (stripped) {
      return stripped;
    }
  }
  return title.trim();
}

export function mapQueueItemRecord(record: QueueItemRecord): WorkQueueItem {
  const data = record.data ?? ({} as QueueItemRecordData);
  const title = data.title ?? record.display ?? record.id;
  return {
    id: record.id,
    request_number: getQueueItemRequestNumber(title, data.purpose),
    created_at: data.queueEnteredAt ?? data.enqueuedAt ?? record.created_at,
    // Customer and address live on the source record (sourceRecordId), not on the queue item
    customer: '',
    city: '',
    state: '',
    zip: '',
    status: getQueueItemStatus(record),
    title,
    queue_id: data.queueId,
    source_record_id: data.sourceRecordId,
    source_definition_id: data.sourceDefinitionId,
    source_definition_display:
      record.expanded?.sourceDefinition?.display ?? undefined,
    priority: data.priority,
    due_at: data.dueAt ?? null,
  };
}
